// External Imports
import { useContext, useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { MdEditSquare, MdRefresh } from "react-icons/md";
import { useSearchParams } from "react-router-dom";

// Internal Imports
import { db } from "../../firebase";
import { UtilityContext } from "../../context/util-context";
import { truncate } from "../../util/format-text";
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import MessageModal from "../customers-page/MessageModal";
import PersonCard from "../UI/PersonCard";
import Chat from "./Chat";

/** ========================================================================
 * * Support Component
 * This component renders a page that allows chatting with customers.
 * ========================================================================== */

export default function Support() {
  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Get Vehicle ID from URL
  const [searchParms] = useSearchParams();
  const id = searchParms.get("id");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [newMessage, setNewMessage] = useState(false);
  const [messages, setMessages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filtered, setFiltered] = useState();

  /* ====================== FETCH MESSAGES ====================== */
  const fetchMessages = async () => {
    try {
      setLoading(true);
      const colRef = collection(db, "Messages");
      const customers = await requestAdminAPI("fetch-all", {
        table: "customer",
      });

      const unsubscribe = onSnapshot(colRef, (snapshot) => {
        let data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          customer: customers.find((c) => c.id === doc.id),
        }));
        data = data.sort((a, b) =>
          a.messages[a.messages.length - 1].time <
          b.messages[b.messages.length - 1].time
            ? 1
            : -1
        );
        setMessages(data);
        setSelected(data[0]);
      });
      setLoading(false);
      return () => unsubscribe();
    } catch (err) {
      setLoading(false);
    }
  };

  /* ====================== FETCH EMPLOYEES ====================== */
  const fetchEmployees = async () => {
    try {
      const response = await requestAdminAPI("fetch-all", {
        table: "employee",
      });
      setEmployees(response);
    } catch (err) {
      console.error(err);
    };
  };

  /* ====================== SEARCH HANDLER ====================== */
  const searchHandler = (e) => {
    e.preventDefault();
    const query = e.target.search.value;
    let results = [];
    query.split(" ").forEach((word) => {
      messages.forEach((m) => {
        let string = `${m.id}`;
        if (m.customer)
          string += ` ${m.customer.firstName} ${m.customer.lastName}`;
        if (string.toLowerCase().includes(word.toLowerCase())) results.push(m);
      });
    });
    setFiltered(results);
    setSelected(results[0] || null);
  };

  /* ====================== CLEAR HANDLER ====================== */
  const clearHandler = () => {
    setFiltered();
    document.getElementById("search").value = "";
    setSelected(messages[0]);
  };

  // Fetch Messages
  useEffect(() => {
    fetchMessages();
    fetchEmployees();
  }, []);

  // Set Selected
  useEffect(() => {
    if (id && messages.length > 0) {
      const selected = messages.find((m) => m.id === id);
      if (selected) setSelected(selected);
      else setNewMessage(true);
    }
  }, [messages]);

  return (
    <div className="divider">
      <MessageModal
        open={newMessage}
        setOpen={setNewMessage}
        onComplete={fetchMessages}
        customerId={id}
      />
      <div className="container text-sm md:col-span-3">
        <Heading
          title="Support"
          isBackArrow={true}
          text="Customer can send these messages only from the mobile app.
           However, any message sent to a customer is also delivered as a text
            message if they have text notifications on."
        >
          <MdRefresh className="icon" onClick={fetchMessages} />
        </Heading>

        <div className="relative">
          {selected?.customer && (
            <div
              className="absolute top-0 left-0 w-[100%] p-4 backdrop-blur-lg clickable"
              onClick={(e) => navigate(`/customers/info?id=${selected.id}`, e)}
            >
              <PersonCard
                label={"Customer:"}
                name={`${selected.customer?.firstName} ${selected.customer?.lastName}`}
                secondary={selected.customer.phone}
                image={selected.customer.photo}
                loading={loading}
              />
            </div>
          )}
          <Chat selected={selected} loadingChat={loading} employees={employees} />
        </div>
      </div>
      <div className="container">
        <div className="flex items-center justify-between">
          <p className="font-bold text-stone-200">Messages</p>
          <MdEditSquare className="icon" onClick={() => setNewMessage(true)} />
        </div>

        <form
          onSubmit={searchHandler}
          className="flex items-center space-x-2 text-xs"
        >
          <input
            className="w-full text-base border border-yellow-600 rounded-md input"
            type="text"
            name="search"
            id="search"
            autoComplete="off"
            required
          />
          <button type="submit" className="text-base btn-primary lg:text-xs">
            Search
          </button>
          <div
            onClick={clearHandler}
            className="text-base btn-secondary lg:text-xs"
          >
            Clear
          </div>
        </form>
        {loading ? (
          <div className="grid gap-4">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="h-12 btn-secondary animate-pulse"></div>
            ))}
          </div>
        ) : !filtered || filtered?.length > 0 ? (
          <div className="gap-2 text-sm list max-h-[100%]">
            {(filtered || messages).map((conv, idx) => (
              <div
                key={idx}
                onClick={() => {
                  navigate(`/support?id=${conv.id}`);
                  setSelected(conv);
                }}
                className={`${
                  selected.id === conv.id && "bg-yellow-600"
                } px-4 py-3 rounded-md relative bg-stone-800 grid cursor-pointer`}
              >
                <p className="font-semibold text-stone-200">
                  {truncate(
                    conv.customer
                      ? `${conv.customer.firstName} ${conv.customer.lastName}`
                      : conv.id
                  )}
                </p>
                <p className="text-stone-400">
                  {truncate(
                    conv.messages[conv.messages.length - 1].message,
                    36
                  )}
                </p>
                {conv.messages[conv.messages.length - 1].user && (
                  <div className="absolute w-3 h-3 bg-red-700 rounded-full -left-4 -top-1"></div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-center text-stone-400">No Results</p>
        )}
      </div>
    </div>
  );
}
